import { CURRENCY, CURRENCY_LOCALE } from '../config/env';

/** 금액 — 통화 기호 포함. formatMoney(1234.5) → "A$1,234.50" */
export function formatMoney(value, opts = {}) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const { decimals = 2, compact = false } = opts;
  try {
    return new Intl.NumberFormat(CURRENCY_LOCALE, {
      style: 'currency',
      currency: CURRENCY,
      minimumFractionDigits: compact ? 0 : decimals,
      maximumFractionDigits: decimals,
      notation: compact ? 'compact' : 'standard'
    }).format(Number(value));
  } catch (e) {
    return `${CURRENCY} ${Number(value).toFixed(decimals)}`;
  }
}

/** 숫자 — 천 단위 구분. formatNum(12345.678, 1) → "12,345.7" */
export function formatNum(value, decimals = 0) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return new Intl.NumberFormat(CURRENCY_LOCALE, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(Number(value));
}

/** 날짜 — "12 Apr 2025" */
export function formatDate(value, opts = {}) {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...opts
  }).format(d);
}

/** 날짜+시각 — "12 Apr 2025, 09:30" */
export function formatDateTime(value) {
  return formatDate(value, { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** 상대 시각 — "3 min ago" · "yesterday" */
export function timeAgo(value) {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  const diff = Date.now() - d.getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const day = Math.floor(diff / 86400000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  if (h < 24) return `${h} hr${h > 1 ? 's' : ''} ago`;
  if (day === 1) return 'yesterday';
  if (day < 7) return `${day} days ago`;
  if (day < 30) return `${Math.floor(day / 7)} wk ago`;
  return formatDate(d);
}

/** 두께 표기 — 2 → "2.0 mm" */
export function formatThickness(mm) {
  if (mm === null || mm === undefined) return '—';
  return `${Number(mm).toFixed(1)} mm`;
}

/** 파일 크기 */
export function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const trim = n => n.toFixed(1).replace(/\.0$/, '');
  if (bytes < 1048576) return `${trim(bytes / 1024)} KB`;
  return `${trim(bytes / 1048576)} MB`;
}

/** 이니셜 — "Jane Doe" → "JD" */
export function initials(name = '') {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(s => s[0]?.toUpperCase() || '')
    .join('') || 'U';
}

/** 상태 코드 → 사람이 읽는 라벨 */
export const STATUS_LABEL = {
  PENDING: 'Pending',
  CONFIRMED: 'Confirmed',
  IN_PRODUCTION: 'In production',
  SHIPPED: 'Shipped',
  DELIVERED: 'Delivered',
  CANCELLED: 'Cancelled',
  DRAFT: 'Draft',
  SUBMITTED: 'Submitted',
  REVIEWED: 'Under review',
  APPROVED: 'Approved',
  REJECTED: 'Declined',
  MILL_CERT: 'Mill certificate',
  TEST_REPORT: 'Test report',
  COMPLIANCE_CERT: 'Compliance cert',
  ORDER_UPDATE: 'Order',
  CERTIFICATE_READY: 'Certificate',
  ESTIMATION_REPLY: 'Quote',
  SYSTEM: 'System'
};

export const statusLabel = code => STATUS_LABEL[code] || code || '—';

/** 주문 상태의 진행 순서 — 타임라인 렌더링에 사용 */
export const ORDER_FLOW = ['PENDING', 'CONFIRMED', 'IN_PRODUCTION', 'SHIPPED', 'DELIVERED'];
